<script lang="ts">
  /**
   * Tänään: hero ring, meal cards, exercise chips, morning weight.
   * Also serves /paiva/:date for editing a past day.
   */
  import { apiFetch, APIError } from '../lib/api';
  import { navigate } from '../lib/router';
  import { errorMessage } from '../lib/errors';
  import { fmtDay, fmtKg, isDateKey, parseDecimal, todayKey } from '../lib/format';
  import { plans } from '../lib/plans.svelte';
  import { toast } from '../lib/toast.svelte';
  import { EXERCISE_TYPES, type Day, type DayPatch, type ExerciseType } from '../lib/types';
  import Icon from '../components/Icon.svelte';
  import LoadState from '../components/LoadState.svelte';
  import MealCard from '../components/MealCard.svelte';
  import Pill from '../components/Pill.svelte';

  let { params = {} }: { params?: Record<string, string> } = $props();

  const MESSAGES = [
    'Aloitetaan päivä.',
    'Hyvä alku!',
    'Puolimatkassa.',
    'Hyvin menee, jatka samaan malliin.',
    'Enää yksi ateria.',
    'Koko päivä ohjelman mukaan!',
  ];
  const RING = 263.9;

  let date = $derived(params.date && isDateKey(params.date) ? params.date : todayKey());
  let badDate = $derived(Boolean(params.date) && !isDateKey(params.date ?? ''));
  let isToday = $derived(date === todayKey());

  let day = $state<Day | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let saving = $state(false);

  let loadedFor = '';

  async function load(): Promise<void> {
    const target = date;
    loading = true;
    error = null;
    try {
      const d = await apiFetch<Day>(`/api/days/${target}`);
      if (target === date) { day = d; loadedFor = target; }
    } catch (err) {
      if (target === date) error = errorMessage(err);
    } finally {
      if (target === date) loading = false;
    }
  }

  $effect(() => {
    if (badDate) return;
    if (loadedFor !== date) {
      day = null;
      void load();
    }
  });

  async function patch(body: DayPatch, okMessage: string): Promise<void> {
    if (saving) return;
    saving = true;
    try {
      day = await apiFetch<Day>(`/api/days/${date}`, { method: 'PATCH', body });
      toast.show(okMessage);
    } catch (err) {
      toast.show(err instanceof APIError ? errorMessage(err, 'Tallennus epäonnistui') : 'Tallennus epäonnistui');
    } finally {
      saving = false;
    }
  }

  function pickExercise(type: ExerciseType): void {
    if (!day) return;
    if (day.exercise?.type === type) {
      void patch({ exercise: null }, 'Liikunta poistettu');
    } else {
      void patch({ exercise: { type, minutes: day.exercise?.minutes ?? 45 } }, 'Liikunta merkattu');
    }
  }

  function changeMinutes(e: Event): void {
    if (!day?.exercise) return;
    const raw = (e.currentTarget as HTMLInputElement).value;
    const n = parseDecimal(raw);
    const minutes = n === null || n <= 0 ? null : Math.round(n);
    void patch({ exercise: { type: day.exercise.type, minutes } }, 'Kesto tallennettu');
  }

  function changeWeight(e: Event): void {
    const raw = (e.currentTarget as HTMLInputElement).value;
    const kg = parseDecimal(raw);
    if (kg !== null && (kg <= 20 || kg >= 400)) {
      toast.show('Tarkista paino');
      return;
    }
    void patch({ weightKg: kg }, kg === null ? 'Paino poistettu' : 'Paino tallennettu');
  }

  let score = $derived(day?.score ?? 0);
  let ringColor = $derived(
    score === 5 ? 'var(--viisi-good)' : score >= 3 ? 'var(--viisi-warn)' : score >= 1 ? 'var(--viisi-bad)' : 'var(--viisi-accent)'
  );
  let nextMeal = $derived(day?.meals.find((m) => !m.complete) ?? null);
  let planTitle = $derived(plans.titleOf(day?.planId));
</script>

<div class="topbar">
  <div class="grow">
    <div class="sub">{isToday ? fmtDay(date) : 'Muokkaat mennyttä päivää'}</div>
    <h1>{isToday ? 'Tänään' : fmtDay(date)}</h1>
  </div>
  {#if !isToday}
    <button type="button" class="back" title="Takaisin tähän päivään" aria-label="Takaisin tähän päivään" onclick={() => navigate('/')}>
      <Icon name="undo" />
    </button>
  {/if}
</div>

{#if badDate}
  <LoadState error="Virheellinen päivämäärä." onretry={() => navigate('/')} />
{:else if !day}
  <LoadState {loading} {error} onretry={load} />
{:else}
  <div class="card hero">
    <div class="ring">
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="42" fill="none" stroke="var(--viisi-surface-2)" stroke-width="9" />
        <circle
          cx="50" cy="50" r="42" fill="none"
          stroke={ringColor} stroke-width="9" stroke-linecap="round"
          stroke-dasharray={RING} stroke-dashoffset={RING * (1 - score / 5)}
        />
      </svg>
      <div class="lbl"><div><b class="num">{score}/5</b><br /><small>ateriaa</small></div></div>
    </div>
    <div>
      <h2>{MESSAGES[score] ?? MESSAGES[0]}</h2>
      <p>{score < 5 && nextMeal ? `Seuraavaksi: ${nextMeal.name.toLowerCase()}` : 'Kaikki viisi ateriaa merkattu.'}</p>
      <div class="pills">
        <Pill tone="accent">{planTitle}</Pill>
        {#if day.exercise}
          <Pill tone="move"><Icon name="move" /> {day.exercise.type}{day.exercise.minutes ? ` ${day.exercise.minutes} min` : ''}</Pill>
        {:else}
          <Pill>Ei liikuntaa vielä</Pill>
        {/if}
        {#if day.weightKg !== null}
          <Pill num>{fmtKg(day.weightKg)} kg</Pill>
        {/if}
      </div>
    </div>
  </div>

  <h2 class="section-title">Ateriat</h2>
  <div class="stack">
    {#each day.meals as meal (meal.mealNo)}
      <MealCard {meal} onopen={(n) => navigate(`/ateria/${date}/${n}`)} />
    {/each}
  </div>
  <div class="legend-mini">
    <span><i class="lg-h"></i>Hiilihydraatti</span>
    <span><i class="lg-p"></i>Proteiini</span>
    <span><i class="lg-r"></i>Rasva</span>
    <span><i class="lg-v"></i>Vihannekset</span>
  </div>

  <h2 class="section-title">Liikunta</h2>
  <div class="card move-card" aria-busy={saving}>
    <div class="row move-head">
      <div class="grow">
        <div class="move-title">{day.exercise ? `Liikuit: ${day.exercise.type}` : isToday ? 'Liikuitko tänään?' : 'Liikuitko tänä päivänä?'}</div>
        <div class="move-sub">{day.exercise ? 'Hienoa! Näkyy kalenterissa ja kehityksessä.' : 'Merkkaa laji, niin se näkyy kalenterissa ja kehityksessä.'}</div>
      </div>
    </div>
    <div class="chips">
      {#each EXERCISE_TYPES as t (t)}
        <button
          type="button"
          class="chip"
          class:on={day.exercise?.type === t}
          aria-pressed={day.exercise?.type === t}
          disabled={saving}
          onclick={() => pickExercise(t)}
        >{t}</button>
      {/each}
    </div>
    {#if day.exercise}
      <div class="minutes">
        <label for="move-minutes">Kesto</label>
        <input
          class="box-in"
          type="number"
          inputmode="numeric"
          id="move-minutes"
          min="0"
          step="5"
          value={day.exercise.minutes ?? ''}
          disabled={saving}
          onchange={changeMinutes}
        />
        <span class="unit">min</span>
      </div>
    {/if}
  </div>

  <h2 class="section-title">Paino</h2>
  <div class="card weight-row">
    <div>
      <label class="lbl" for="w-in">Aamupaino</label>
      <div class="sub">Kirjaa kun muistat, käyrä on Kehitys-sivulla.</div>
    </div>
    <div class="row">
      <input
        class="box-in"
        id="w-in"
        type="number"
        inputmode="decimal"
        step="0.1"
        min="0"
        placeholder="0,0"
        value={day.weightKg ?? ''}
        disabled={saving}
        onchange={changeWeight}
      />
      <span class="unit">kg</span>
    </div>
  </div>
{/if}

<style>
  .topbar {
    position: sticky;
    top: 0;
    z-index: 5;
    background: linear-gradient(var(--viisi-ground) 80%, transparent);
    padding: 18px 0 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .topbar h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.01em; flex: 1; }
  .topbar .sub { color: var(--viisi-ink-2); font-size: 13px; }
  .grow { flex: 1; min-width: 0; }
  .back {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--viisi-surface-2);
    display: grid;
    place-items: center;
  }
  .back :global(svg) { width: 18px; height: 18px; }

  .card {
    background: var(--viisi-surface);
    border-radius: var(--viisi-radius);
    padding: 14px;
    box-shadow: var(--viisi-shadow);
  }
  .stack { display: flex; flex-direction: column; gap: 10px; }
  .row { display: flex; align-items: center; gap: 10px; }
  .section-title {
    margin: 22px 0 10px;
    font-size: 17px;
    font-weight: 700;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }

  .hero { display: grid; grid-template-columns: 100px 1fr; gap: 14px; align-items: center; }
  .ring { position: relative; width: 100px; height: 100px; }
  .ring svg { width: 100px; height: 100px; transform: rotate(-90deg); }
  .ring circle { transition: stroke-dashoffset 0.4s ease; }
  .ring .lbl {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    text-align: center;
    font-family: var(--viisi-font-display);
  }
  .ring .lbl b { font-size: 28px; font-weight: 800; line-height: 1; }
  .ring .lbl small { font-size: 11px; color: var(--viisi-ink-2); font-weight: 600; }
  .hero h2 { font-size: 19px; font-weight: 700; margin-bottom: 4px; }
  .hero p { margin: 0; color: var(--viisi-ink-2); font-size: 13px; }
  .pills { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }

  .legend-mini { display: flex; gap: 10px; font-size: 11px; color: var(--viisi-ink-3); margin: 6px 2px 0; flex-wrap: wrap; }
  .legend-mini i {
    display: inline-block;
    width: 10px;
    height: 6px;
    border-radius: 3px;
    margin-right: 4px;
    vertical-align: middle;
  }
  .lg-h { background: var(--viisi-c-carb); }
  .lg-p { background: var(--viisi-c-prot); }
  .lg-r { background: var(--viisi-c-fat); }
  .lg-v { background: var(--viisi-c-veg); }

  .move-card { border-left: 4px solid var(--viisi-move); }
  .move-head { margin-bottom: 10px; }
  .move-title { font-weight: 700; }
  .move-sub { font-size: 12.5px; color: var(--viisi-ink-2); }
  .chips { display: flex; flex-wrap: wrap; gap: 8px; }
  .chip {
    padding: 8px 12px;
    border-radius: 999px;
    background: var(--viisi-surface-2);
    font-weight: 600;
    font-size: 13px;
    color: var(--viisi-ink-2);
    border: 1.5px solid transparent;
  }
  .chip.on { background: var(--viisi-move); color: var(--viisi-move-ink); }
  .chip:disabled { cursor: progress; }
  .minutes { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
  .minutes label { font-size: 12.5px; color: var(--viisi-ink-2); font-weight: 600; }
  .minutes input { width: 70px; text-align: right; }
  .unit { font-size: 12.5px; color: var(--viisi-ink-2); font-weight: 600; }

  .box-in {
    padding: 9px 12px;
    border-radius: var(--viisi-radius-sm);
    border: 1px solid var(--viisi-line);
    background: var(--viisi-surface-2);
  }

  .weight-row { display: grid; grid-template-columns: 1fr auto; gap: 12px; align-items: center; }
  .weight-row input {
    width: 110px;
    text-align: right;
    font-family: var(--viisi-font-display);
    font-weight: 700;
    font-size: 18px;
  }
  .weight-row .lbl { font-weight: 700; }
  .weight-row .sub { font-size: 12.5px; color: var(--viisi-ink-2); }
</style>
